import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translate(-50%, -48%); }
  to { opacity: 1; transform: translate(-50%, -50%); }
`;

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex.modalBackdrop};
  background-color: rgba(0, 0, 0, 0.4);
  animation: ${fadeIn} ${({ theme }) => theme.transitions.fast};
`;

export const Dialog = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: ${({ theme }) => theme.zIndex.modal};
  width: 100%;
  max-width: 26.25rem;
  background-color: ${({ theme }) => theme.colors.bgSurface};
  border-radius: ${({ theme }) => theme.radii.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  animation: ${slideUp} ${({ theme }) => theme.transitions.base};
  padding: ${({ theme }) => `${theme.spacing[8]} ${theme.spacing[6]}`};

  &:focus {
    outline: none;
  }
`;

export const IconCircle = styled.div<{ $variant: 'danger' | 'warning' | 'info' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.25rem;
  height: 3.25rem;
  border-radius: ${({ theme }) => theme.radii.full};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  font-size: 1.5rem;
  background-color: ${({ theme, $variant }) => {
    const map = { danger: theme.colors.danger, warning: theme.colors.warning, info: theme.colors.info };
    return `${map[$variant]}14`;
  }};
  color: ${({ theme, $variant }) => {
    const map = { danger: theme.colors.danger, warning: theme.colors.warning, info: theme.colors.info };
    return map[$variant];
  }};
`;

export const Title = styled.h3`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[2]};
`;

export const Message = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  margin: 0 0 ${({ theme }) => theme.spacing[6]};
`;

export const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: flex-end;
`;

const BaseButton = styled.button`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[5]}`};
  border-radius: ${({ theme }) => theme.radii.lg};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

export const CancelButton = styled(BaseButton)`
  background-color: ${({ theme }) => theme.colors.bgMuted};
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.gray200};

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray200};
  }
`;

export const ConfirmButton = styled(BaseButton)<{ $variant: 'danger' | 'warning' | 'info' }>`
  border: none;
  color: ${({ theme }) => theme.colors.textInverse};
  background-color: ${({ theme, $variant }) => {
    const map = { danger: theme.colors.danger, warning: theme.colors.warning, info: theme.colors.info };
    return map[$variant];
  }};

  &:hover {
    opacity: 0.9;
  }
`;
